import * as React from 'react'
import Autocomplete, { AutocompleteInputChangeReason, AutocompleteRenderGetTagProps } from '@mui/material/Autocomplete'
import { ChipProps } from '@mui/material/Chip'
import Checkbox from '@mui/material/Checkbox'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import ClearIcon from '@mui/icons-material/Clear'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import TextField from '@mui/material/TextField'
import { TextFieldProps } from './TextField'
import { DropDownOptions } from 'types/common'
import { Tooltip } from 'components/ui'
import { isDropdownOptions, isOptionEqualToValue, getFilteredOptions } from './MultiSelectField.helpers'

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

export type MultiSelectedValues = {
  value: string
  label: string
}

export interface IMultiSelectProps extends Omit<TextFieldProps, 'value' | 'onChange'> {
  options?: DropDownOptions[]
  name?: string
  value?: DropDownOptions[] | string[] | string
  // value?: DropDownOptions | string[] | string
  hightLightColor?: string
  freeSolo?: boolean
  placeholder?: string
  readOnlyInput?: boolean
  chipProps?: ChipProps
  disablePortal?: boolean
  multiple?: boolean
  disableCloseOnSelect?: boolean
  disableClearable?: boolean
  openOnFocus?: boolean
  inputRef?: any
  tooltipTitle?: string
  renderTags?: (value: any[], getTagProps: AutocompleteRenderGetTagProps) => React.ReactNode
  onOpen?: (e: React.SyntheticEvent<Element, Event>) => void
  onClose?: (e: React.SyntheticEvent<Element, Event>) => void
  onInputChange?: (e: React.SyntheticEvent<Element, Event>, val: string, reason: AutocompleteInputChangeReason) => void
  onChange?: (e: React.SyntheticEvent<Element, Event>, val: any) => void
  setValue?: (name: string, value: any) => void
  width?: string
  txtInputId?: string
  withCheckbox?: boolean
  loading?: boolean
}

const MultiSelectField: React.FunctionComponent<IMultiSelectProps> = (props) => {
  const {
    txtInputId,
    label,
    options,
    onInputChange,
    onChange,
    renderTags,
    multiple = true,
    value,
    // value = multiple ? [] : '', // ensure value is always defined to avoid the noncontrolled component warning
    error,
    helperText,
    disabled,
    freeSolo = false,
    className,
    placeholder,
    disablePortal = false,
    readOnlyInput = false,
    disableCloseOnSelect = false,
    disableClearable = false,
    openOnFocus = false,
    chipProps,
    inputRef,
    tooltipTitle = 'clear',
    onOpen,
    onClose,
    onBlur,
    width,
    withCheckbox = true,
    loading,
    sx,
  } = props

  const filteredOptions = React.useMemo(() => getFilteredOptions(options, value, multiple), [options, value, multiple])

  return (
    <Autocomplete
      id={txtInputId}
      options={filteredOptions}
      value={value}
      style={{ width: width }}
      disabled={disabled}
      freeSolo={freeSolo}
      ChipProps={chipProps}
      renderTags={renderTags}
      isOptionEqualToValue={isOptionEqualToValue}
      clearIcon={
        <Tooltip title={tooltipTitle}>
          <ClearIcon fontSize="small" />
        </Tooltip>
      }
      onOpen={onOpen}
      onClose={onClose}
      loading={loading}
      onInputChange={(event, newInputValue, reason) => {
        onInputChange && onInputChange(event, newInputValue, reason)
      }}
      onChange={(e, val) => {
        onChange && onChange(e, val)
      }}
      renderOption={
        (props, option, state) => {
            const { key, ...listItemProps } = props as typeof props & { key: React.Key }
            if (multiple) {
              let selected = state.selected
              if (isDropdownOptions(value)) {
                selected = value.some((v) => v.value === option.value)
              }
              return withCheckbox ? (
                <li key={option.value} {...listItemProps}>
                  <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                  {option.label}
                </li>
              ) : (
                <li key={option.value} {...listItemProps}>
                  {option.label}
                </li>
              )
            }
            return <li key={option.value} {...listItemProps}>{option.label}</li>
          }
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          label={label}
          error={error}
          helperText={helperText}
          disabled={disabled}
          className={className}
          onBlur={onBlur}
          inputRef={inputRef}
          sx={sx}
        />
      )}
      onKeyDown={(e) => readOnlyInput && e.preventDefault()}
      multiple={multiple}
      disablePortal={disablePortal}
      disableCloseOnSelect={disableCloseOnSelect}
      disableClearable={disableClearable}
      openOnFocus={openOnFocus}
      disableListWrap
    />
  )
}

export default MultiSelectField
